import { useState, useRef, useEffect } from "react";
import Badge from "@mui/material/Badge";
import { Box, Typography, Tabs, Tab, Select, MenuItem, Stack } from "@mui/material";
import Popover from "@mui/material/Popover";
import {
  Check as AllEcrfCompleted,
  PriorityHigh as EcrfHasIssue,
  Remove as NotEcrfTouched,
  Circle as VisitStatusIcon,
  HelpOutline as HelpIcon,
} from "@mui/icons-material";
import getCustomNames from "../../utils/customNames";
import { useEcrfContext } from "../../context/EcrfContext";
import { VISIT_STATUSES, ECRF_STATUSES } from "../../utils/constants";
import useGetVisitsDropdownStatus from "../../hooks/useGetVisitsDropdownStatus";
import useGetVisitSchedule from "../../hooks/useGetVisitSchedule";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const TabsWithDropdown = ({ numberOfVisitsArray, handleTabChange, value, setValue }) => {
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;

  const { selectedVisit, setSelectedVisit, allVisits, patientId, trial } = useEcrfContext();

  const [dropdownValue, setDropdownValue] = useState("");
  const [visitsDisabled, setVisitsDisabled] = useState(false);
  const [newVisits, setNewVisits] = useState([0]);
  const [currentVisit, setCurrentVisit] = useState({});
  const [numberOfIncompleteVisits, setNumberOfIncompleteVisits] = useState(new Set());
  const isBaselineScheduled = useRef(true);
  const visitTabs = useRef({ 0: 0 });
  const visitMenus = useRef({});
  const prevTrialIdRef = useRef(trial?.id);

  // Popover states and functions
  const [anchorEl, setAnchorEl] = useState(null);
  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const openPopOver = Boolean(anchorEl);

  const { data: downdownVisitsStatus } = useGetVisitsDropdownStatus({
    trialId: trial?.id,
    patientId: patientId,
    enabled: !!patientId && !!trial?.id,
  });

  const { data: visitScheduleData } = useGetVisitSchedule({
    visitId: selectedVisit?.id,
    patientId: patientId,
    enabled: !!patientId,
  });

  useEffect(() => {
    visitTabs.current = { 0: 0 };

    const sortedNumberOfVisitArrays = numberOfVisitsArray.sort().reverse();

    sortedNumberOfVisitArrays.forEach((number) => {
      if (number < 0 && number !== -3) {
        const key = number.toString();

        if (!(key in visitTabs.current)) {
          visitTabs.current[key] = Object.keys(visitTabs.current).length;
        }
      }
    });
  }, [numberOfVisitsArray]);

  useEffect(() => {
    if (value === 0) {
      let hasDropdown = false;
      let updatedSelectedVisit = {};
      let defaultVisitName = "";

      for (let visit of allVisits) {
        if (dropdownValue === visit.visit_name) {
          hasDropdown = true;
        }

        if (visit.visit_number === selectedVisit.visit_number) updatedSelectedVisit = visit;

        if (visit.visit_number === 0) defaultVisitName = visit.visit_name;
        if (visit.visit_number === -3) defaultVisitName = visit.visit_name;
      }

      if (!hasDropdown) {
        setDropdownValue(defaultVisitName);
      }

      numberOfVisitsArray.forEach((visitNumber) => {
        if (visitNumber === selectedVisit.visit_number) {
          let visit = allVisits.filter((visit) => visit.visit_number === visitNumber)[0];

          if (!visit) {
            visit = {
              visit_number: visitNumber,
              visit_name: updatedSelectedVisit.visit_name || getCustomNames(visitNumber),
            };
          }

          setCurrentVisit(visit);
        }
      });
    }
  }, [selectedVisit, numberOfVisitsArray]);

  useEffect(() => {
    const visitsWithEcrfIssues = new Set();

    const hasBaselineScheduledInfo = allVisits.some((visit) => "is_baseline_scheduled" in visit);
    if (patientId && !hasBaselineScheduledInfo) {
      isBaselineScheduled.current = true;
      setVisitsDisabled(false);
    }

    allVisits.forEach((visit) => {
      if (patientId && "is_baseline_scheduled" in visit) {
        isBaselineScheduled.current = visit?.is_baseline_scheduled;
        setVisitsDisabled(!visit.is_baseline_scheduled);
      }

      const visitStatus = downdownVisitsStatus?.find(
        (dropdownVisit) => visit.id === dropdownVisit.id
      );

      if (isBaselineScheduled.current) {
        if (visit.visit_number === -3 || visit.visit_number >= 0) {
          if (visitStatus?.ecrf_status === ECRF_STATUSES.INCOMPLETE) {
            visitsWithEcrfIssues.add(visit.visit_name);
          }
        }
      }

      if (visit.visit_number === 0 && dropdownValue === "" && value === 0) {
        setDropdownValue((prev) => (prev === "" ? visit.visit_name : prev));
      }

      if (visit.visit_number === -3 && dropdownValue === "" && value === 0) {
        setDropdownValue(visit.visit_name);

        if (selectedVisit.visit_number !== visit.visit_number && value === 0) {
          handleTabChange(0, -3, 0);
          setValue(0);
        }
      }
    });

    setNumberOfIncompleteVisits(visitsWithEcrfIssues);
  }, [allVisits, downdownVisitsStatus]);

  useEffect(() => {
    const trialChanged = prevTrialIdRef.current !== trial?.id;
    prevTrialIdRef.current = trial?.id;

    let hasScreeningInAllVisits = false;

    const allVisitNumbers = allVisits.map((visit) => {
      if (visit.visit_number === -3) {
        hasScreeningInAllVisits = true;
      }
      return visit.visit_number;
    });
    const missingVisits = numberOfVisitsArray.filter(
      (visitNumber) => !allVisitNumbers.includes(visitNumber)
    );

    if (!patientId) {
      if (currentVisit.visit_name) {
        if (currentVisit.visit_number < 0 && currentVisit.visit_number !== -3 && value === 0) {
          if (visitTabs.current[currentVisit.visit_number?.toString()]) {
            handleTabChange(
              0,
              currentVisit.visit_number,
              visitTabs.current[currentVisit.visit_number?.toString()]
            );
            setValue(visitTabs.current[currentVisit.visit_number?.toString()]);
          } else {
            setSelectedVisit(allVisits.filter((visit) => visit.visit_number === 0)[0]);
          }
        }
      } else {
        if (currentVisit.visit_number < 0 && currentVisit.visit_number !== -3 && value === 0) {
          handleTabChange(
            0,
            currentVisit.visit_number,
            visitTabs.current[currentVisit.visit_number?.toString()]
          );
          setValue(visitTabs.current[currentVisit.visit_number?.toString()]);
        }
      }

      if (selectedVisit.visit_number >= 0 || selectedVisit.visit_number === -3) {
        setDropdownValue(currentVisit.visit_name);
      }
    }

    if (
      !trialChanged &&
      !hasScreeningInAllVisits &&
      numberOfVisitsArray.includes(-3) &&
      value === 0
    ) {
      handleTabChange(0, -3, 0);
      setValue(0);
      setDropdownValue(getCustomNames(-3));
    }

    setNewVisits(missingVisits);
  }, [allVisits, numberOfVisitsArray]);

  useEffect(() => {
    if (selectedVisit.visit_number >= 0 || selectedVisit.visit_number === -3) {
      setDropdownValue(currentVisit.visit_name);
    }
  }, [currentVisit]);

  const specialUnscheduledCounts = allVisits
    ?.filter((visit) => visit.visit_type === "Unscheduled Event")
    .map((visit) => visit.special_unscheduled_count);
  const specialAdverseCounts = allVisits
    ?.filter((visit) => visit.visit_type === "Adverse Event")
    .map((visit) => visit.special_adverse_count);

  const renderLabel = (visitLabel, number, patientId) => {
    let badgeContent = null;

    if (
      number === -2 &&
      patientId !== undefined &&
      specialUnscheduledCounts &&
      specialUnscheduledCounts.length > 0
    ) {
      badgeContent = specialUnscheduledCounts;
    } else if (
      number === -1 &&
      patientId !== undefined &&
      specialAdverseCounts &&
      specialAdverseCounts.length > 0
    ) {
      badgeContent = specialAdverseCounts;
    }

    if (patientId === undefined) {
      return (
        <div
          style={{
            position: "relative",
            display: "inline-block",
            paddingRight: "10px",
          }}
        >
          {visitLabel}
        </div>
      );
    } else {
      let visitStatus = null;

      visitStatus = downdownVisitsStatus?.find(
        (dropdownVisit) => number === dropdownVisit.visit_number
      );

      let ecrfStatusIcon = <></>;
      let visitStatusIcon = <></>;

      if (visitStatus) {
        if (visitStatus.ecrf_status === ECRF_STATUSES.COMPLETED) {
          ecrfStatusIcon = <AllEcrfCompleted sx={{ color: "green", ml: 2, fontSize: 16 }} />;
        } else if (visitStatus.ecrf_status === ECRF_STATUSES.NEUTRAL) {
          ecrfStatusIcon = <NotEcrfTouched sx={{ color: "grey", ml: 2, fontSize: 16 }} />;
        } else {
          ecrfStatusIcon = <EcrfHasIssue sx={{ color: "red", ml: 2, fontSize: 16 }} />;
        }

        if (visitStatus.visit_number === -2 || visitStatus.visit_number === -1) {
          if (visitStatus.overall_visit_status === VISIT_STATUSES.NOT_OPENED) {
            visitStatusIcon = <VisitStatusIcon sx={{ color: "grey", ml: 2, fontSize: 12 }} />;
          } else if (visitStatus.overall_visit_status === VISIT_STATUSES.INCOMPLETE) {
            visitStatusIcon = <VisitStatusIcon sx={{ color: "red", ml: 2, fontSize: 12 }} />;
          } else if (visitStatus.overall_visit_status === VISIT_STATUSES.SKIPPED) {
            visitStatusIcon = <VisitStatusIcon sx={{ color: "orange", ml: 2, fontSize: 12 }} />;
          } else if (visitStatus.overall_visit_status === VISIT_STATUSES.COMPLETED) {
            visitStatusIcon = <VisitStatusIcon sx={{ color: "green", ml: 2, fontSize: 12 }} />;
          }
        }
      }

      return (
        <div
          style={{
            position: "relative",
            display: "inline-block",
            paddingRight: "10px",
          }}
        >
          {visitLabel} {ecrfStatusIcon} {visitStatusIcon}
          {badgeContent !== null ? (
            <Badge
              badgeContent={badgeContent}
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: visitLabel === "Unscheduled Event" ? "red" : "blue",
                  color: "white",
                  position: "absolute",
                  top: "-20px",
                  right: "-25px",
                  transform: "none",
                  zIndex: 1,
                },
              }}
            />
          ) : (
            <Badge
              badgeContent={0}
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: "gray",
                  color: "white",
                  position: "absolute",
                  top: "-20px",
                  right: "-25px",
                  transform: "none",
                  zIndex: 1,
                },
              }}
            />
          )}
        </div>
      );
    }
  };

  const handleDropdownChange = (event) => {
    const newValue = event.target.value;
    setDropdownValue(newValue);
  };

  return (
    <Tabs value={value} aria-label="basic tabs example">
      <Tab
        id={`normal-visits-tab`}
        label={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            sx={{
              overflow: "auto",
            }}
          >
            <Select
              value={dropdownValue}
              onChange={handleDropdownChange}
              variant="standard"
              disableUnderline
              SelectDisplayProps={{ "data-testid": "visit-dropdown-select" }}
            >
              {allVisits.length > 0 &&
                allVisits.map((visit) => {
                  let ecrfStatusIcon;
                  let visitStatusIcon;

                  if (patientId) {
                    const visitStatus = downdownVisitsStatus?.find(
                      (dropdownVisit) => visit.id === dropdownVisit.id
                    );

                    if (visitStatus) {
                      if (visitStatus.ecrf_status === ECRF_STATUSES.COMPLETED) {
                        ecrfStatusIcon = (
                          <AllEcrfCompleted sx={{ color: "green", ml: 2, fontSize: 16 }} />
                        );
                      } else if (visitStatus.ecrf_status === ECRF_STATUSES.NEUTRAL) {
                        ecrfStatusIcon = (
                          <NotEcrfTouched sx={{ color: "grey", ml: 2, fontSize: 16 }} />
                        );
                      } else {
                        ecrfStatusIcon = (
                          <EcrfHasIssue sx={{ color: "red", ml: 2, fontSize: 16 }} />
                        );
                      }

                      if (visitStatus.overall_visit_status === VISIT_STATUSES.NOT_OPENED) {
                        visitStatusIcon = (
                          <VisitStatusIcon sx={{ color: "grey", ml: 2, fontSize: 12 }} />
                        );
                      } else if (visitStatus.overall_visit_status === VISIT_STATUSES.INCOMPLETE) {
                        visitStatusIcon = (
                          <VisitStatusIcon sx={{ color: "red", ml: 2, fontSize: 12 }} />
                        );
                      } else if (visitStatus.overall_visit_status === VISIT_STATUSES.SKIPPED) {
                        visitStatusIcon = (
                          <VisitStatusIcon sx={{ color: "orange", ml: 2, fontSize: 12 }} />
                        );
                      } else if (visitStatus.overall_visit_status === VISIT_STATUSES.COMPLETED) {
                        visitStatusIcon = (
                          <VisitStatusIcon sx={{ color: "green", ml: 2, fontSize: 12 }} />
                        );
                      }
                    }
                  }

                  if ((visit.visit_number >= 0 && !visitsDisabled) || visit.visit_number === -3) {
                    let visitLabel = visit.visit_name;

                    if (!visitLabel) visitLabel = getCustomNames(visit.visit_number);

                    if (!(visit.visit_number.toString() in visitMenus.current)) {
                      visitMenus.current[visitLabel] = visit.visit_number;
                    }

                    return (
                      <MenuItem
                        key={visit.visit_number}
                        value={visitLabel}
                        onClick={(event) => {
                          handleTabChange(event, visit.visit_number, 0);
                          setValue(0);
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="body1">{visitLabel}</Typography>

                          {!!patientId && (
                            <>
                              {visitScheduleData?.has_inclinic_activity && <>{ecrfStatusIcon}</>}{" "}
                              <>{visitStatusIcon}</>
                            </>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  }
                  return null;
                })}

              {!patientId &&
                newVisits
                  .sort((a, b) => a - b)
                  .map((number) => {
                    let visitLabel = "";

                    if (number >= 0 || number === -3) {
                      visitLabel = getCustomNames(number);

                      if (!(number.toString() in visitMenus.current)) {
                        visitMenus.current[visitLabel] = number;
                      }

                      return (
                        <MenuItem
                          key={number}
                          value={visitLabel}
                          onClick={(event) => {
                            handleTabChange(event, number, 0);
                            setValue(0);
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="body1">{visitLabel}</Typography>
                          </Box>
                        </MenuItem>
                      );
                    }
                    return null;
                  })}
            </Select>

            {patientId &&
              numberOfIncompleteVisits.size > 0 &&
              visitScheduleData?.has_inclinic_activity && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "6px",
                    right: "0",
                    minWidth: "20px",
                    height: "20px",
                    bgcolor: "error.main",
                    color: "white",
                    borderRadius: "50%",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.5,
                  }}
                >
                  {numberOfIncompleteVisits.size}
                </Box>
              )}

            {patientId && (
              <>
                <HelpIcon
                  aria-owns={openPopOver ? "mouse-over-popover" : undefined}
                  aria-haspopup="true"
                  onMouseEnter={handlePopoverOpen}
                  onMouseLeave={handlePopoverClose}
                  sx={{
                    position: "absolute",
                    top: "6px",
                    left: "0",
                    color: "black",
                    fontSize: 18,
                  }}
                />

                <Popover
                  id="mouse-over-popover"
                  sx={{ pointerEvents: "none" }}
                  open={openPopOver}
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  onClose={handlePopoverClose}
                  disableRestoreFocus
                >
                  <Box sx={{ p: 1.5 }}>
                    {visitScheduleData?.has_inclinic_activity && (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          ECRF Status
                        </Typography>
                        <Stack spacing={0.5} sx={{ mb: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <NotEcrfTouched sx={{ color: "grey", fontSize: 16 }} />
                            <Typography variant="body2">No ECRF Touched</Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <EcrfHasIssue sx={{ color: "red", fontSize: 16 }} />
                            <Typography variant="body2">At least one ECRF has issue</Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <AllEcrfCompleted sx={{ color: "green", fontSize: 16 }} />
                            <Typography variant="body2">All ECRFs completed</Typography>
                          </Stack>
                        </Stack>
                      </>
                    )}

                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Visit Status
                    </Typography>
                    <Stack spacing={0.5}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <VisitStatusIcon sx={{ color: "grey", fontSize: 12 }} />
                        <Typography variant="body2">Visit Not Opened</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <VisitStatusIcon sx={{ color: "red", fontSize: 12 }} />
                        <Typography variant="body2">Incomplete Visit</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <VisitStatusIcon sx={{ color: "orange", fontSize: 12 }} />
                        <Typography variant="body2">Skipped Visit</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <VisitStatusIcon sx={{ color: "green", fontSize: 12 }} />
                        <Typography variant="body2">Completed Visit</Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Popover>
              </>
            )}
          </Box>
        }
      />

      {numberOfVisitsArray.map((number) => {
        if (
          number < 0 &&
          number !== -3 &&
          !(role === "Reviewer" && (number === -2 || number === -1 || number === -6))
        ) {
          let visitLabel = "";

          for (let visit of allVisits) {
            if (visit.visit_number === number) {
              visitLabel = visit.visit_name;

              if (!visitLabel || !visitLabel.trim()) {
                visitLabel = getCustomNames(number);
              }
            }
          }

          if (!patientId && (!visitLabel || !visitLabel.trim()))
            visitLabel = getCustomNames(number);

          return (
            <Tab
              id={`visit-${number}`}
              key={number}
              label={renderLabel(visitLabel, number, patientId)}
              {...a11yProps(number)}
              onClick={(event) => {
                handleTabChange(event, number, visitTabs.current[number.toString()]);
                setValue(visitTabs.current[number.toString()]);
              }}
            />
          );
        }
        return null;
      })}
    </Tabs>
  );
};

export default TabsWithDropdown;
